/**
 * Node visual "Atribuir Responsável da Tarefa" (task_assign)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Exibe a origem da tarefa (gatilho ou variável) e o modo de atribuição
 * do responsável, seguindo o padrão amber-500 da categoria Tarefas.
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { UserCog } from 'lucide-react';
import { FlowNodeData, TaskAssignConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/** Traduz o modo de atribuição para texto legível em PT-BR */
const getAssigneeLabel = (config?: TaskAssignConfig): string | null => {
  if (!config?.assignee_mode) return null;
  switch (config.assignee_mode) {
    case 'manual':
      return config.assignee_name || 'Usuário fixo';
    case 'deal_seller':
      return 'Vendedor do negócio';
    case 'from_variable':
      return config.assignee_variable ? `{{${config.assignee_variable}}}` : 'Variável';
    case 'clear':
      return 'Remover responsável';
    default:
      return null;
  }
};

export function TaskAssignNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as TaskAssignConfig | undefined;

  const assigneeLabel = getAssigneeLabel(config);
  const isConfigured = !!(
    config?.task_source &&
    assigneeLabel &&
    (config.task_source === 'from_trigger' ||
      (config.task_source === 'from_variable' && config.task_variable))
  );

  const sourceLabel =
    config?.task_source === 'from_variable'
      ? `{{${config.task_variable || 'variavel'}}}`
      : 'Tarefa do gatilho';

  return (
    <>
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white"
      />

      <div
        className={`
          min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
          ${selected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-amber-400'}
          transition-all duration-200
        `}
      >
        <div className="flex items-center gap-2 p-3 border-b border-border bg-amber-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-amber-500">
            <UserCog className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm truncate">
            {config?.nickname || 'Atribuir Responsável'}
          </span>
        </div>

        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Tarefa:</span>
                <span className="font-medium truncate" title={sourceLabel}>
                  {sourceLabel}
                </span>
              </div>
              <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                <span className="text-muted-foreground">Responsável:</span>
                <span className="font-medium truncate" title={assigneeLabel || ''}>
                  {assigneeLabel}
                </span>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar
            </p>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white"
      />
    </>
  );
}
